import React, { useState, useEffect } from 'react';
import { WeeklyPlan, WeeklyPlanCreate, WeeklyAllocationCreate, WeeklyNonProductiveAllocationCreate } from '../types/weekly-plan';
import { Project } from '../types/project';
import Button from './Button';
import { NON_PRODUCTIVE_CATEGORY_OPTIONS } from '../constants/nonProductiveCategories';

interface WeeklyPlanFormProps {
  plan?: WeeklyPlan;
  userId: string;
  projects: Project[];
  onSubmit: (planData: WeeklyPlanCreate) => void;
  onCancel: () => void;
}

const DEFAULT_NON_PRODUCTIVE_CATEGORY_ID = NON_PRODUCTIVE_CATEGORY_OPTIONS[0]?.id ?? '';

const WeeklyPlanForm: React.FC<WeeklyPlanFormProps> = ({ plan, userId, projects, onSubmit, onCancel }) => {
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [allocations, setAllocations] = useState<WeeklyAllocationCreate[]>([]);
  const [nonProductiveAllocations, setNonProductiveAllocations] = useState<WeeklyNonProductiveAllocationCreate[]>([]);
  const [error, setError] = useState<string | null>(null);

  const FIXED_CAPACITY = 40;

  // Helper function to get next Monday
  const getNextMonday = (): string => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysUntilMonday);
    return monday.toISOString().split('T')[0];
  };

  // Helper function to get Friday of the same week
  const getFridayFromMonday = (mondayStr: string): string => {
    const monday = new Date(mondayStr);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return friday.toISOString().split('T')[0];
  };

  // Pre-fill form when editing
  useEffect(() => {
    if (plan) {
      setWeekStartDate(plan.week_start_date);
      setWeekEndDate(plan.week_end_date);
      setNotes(plan.notes || '');
      setAllocations((plan.allocations ?? []).map(item => ({
        project_id: item.project_id,
        hours: item.hours,
        notes: item.notes || undefined,
      })));
      setNonProductiveAllocations((plan.non_productive_allocations || []).map(item => ({
        category_id: item.category_id,
        hours: item.hours,
        notes: item.notes || undefined,
      })));
    } else {
      // Set default to next Monday-Friday
      const monday = getNextMonday();
      setWeekStartDate(monday);
      setWeekEndDate(getFridayFromMonday(monday));
    }
  }, [plan]);

  // Update end date when start date changes
  const handleStartDateChange = (newStartDate: string) => {
    setWeekStartDate(newStartDate);
    if (newStartDate) {
      setWeekEndDate(getFridayFromMonday(newStartDate));
    }
  };

  // Add a new project allocation
  const addProjectAllocation = () => {
    if (projects.length === 0) {
      setError('No projects available. Create a project first.');
      return;
    }
    setAllocations([...allocations, { project_id: projects[0].id.toString(), hours: 0, notes: '' }]);
  };

  // Remove a project allocation
  const removeProjectAllocation = (index: number) => {
    setAllocations(allocations.filter((_, i) => i !== index));
  };

  // Update a project allocation
  const updateProjectAllocation = (index: number, field: keyof WeeklyAllocationCreate, value: any) => {
    const updated = [...allocations];
    updated[index] = { ...updated[index], [field]: value };
    setAllocations(updated);
  };

  // Add a new non-productive allocation
  const addNonProductiveAllocation = () => {
    setNonProductiveAllocations([
      ...nonProductiveAllocations,
      { category_id: DEFAULT_NON_PRODUCTIVE_CATEGORY_ID, hours: 0, notes: '' },
    ]);
  };

  // Remove a non-productive allocation
  const removeNonProductiveAllocation = (index: number) => {
    setNonProductiveAllocations(nonProductiveAllocations.filter((_, i) => i !== index));
  };

  // Update a non-productive allocation
  const updateNonProductiveAllocation = (index: number, field: keyof WeeklyNonProductiveAllocationCreate, value: any) => {
    const updated = [...nonProductiveAllocations];
    updated[index] = { ...updated[index], [field]: value };
    setNonProductiveAllocations(updated);
  };

  // Calculate total planned hours
  const calculateTotalPlannedHours = (): number => {
    const productiveHours = allocations.reduce((sum, item) => sum + item.hours, 0);
    const nonProductiveHours = nonProductiveAllocations.reduce((sum, item) => sum + item.hours, 0);
    return productiveHours + nonProductiveHours;
  };

  // Validate and submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!weekStartDate) {
      setError('Please select a start date (Monday)');
      return;
    }

    const totalPlanned = calculateTotalPlannedHours();
    if (totalPlanned > FIXED_CAPACITY) {
      setError(`Total planned hours (${totalPlanned.toFixed(1)}h) exceeds 40-hour capacity`);
      return;
    }
    if (totalPlanned < FIXED_CAPACITY) {
      setError(`Please allocate the full 40 hours (currently ${totalPlanned.toFixed(1)}h)`);
      return;
    }

    // Check for duplicate projects
    const projectIds = allocations.map(item => item.project_id);
    const uniqueProjectIds = new Set(projectIds);
    if (projectIds.length !== uniqueProjectIds.size) {
      setError('Each project can only be allocated once per week');
      return;
    }

    onSubmit({
      user_id: userId,
      week_start_date: weekStartDate,
      notes: notes || undefined,
      allocations: allocations.filter(item => item.hours > 0), // Only include items with hours
      non_productive_allocations: nonProductiveAllocations.filter(item => item.hours > 0),
    });
  };

  const totalPlanned = calculateTotalPlannedHours();
  const remainingHours = FIXED_CAPACITY - totalPlanned;
  const percentageUsed = (totalPlanned / FIXED_CAPACITY) * 100;

  return (
    <form onSubmit={handleSubmit} className="card">
      <div className="mb-4 pb-4 border-b-2 border-gray-100 dark:border-gray-700">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0">{plan ? 'Edit Weekly Plan' : 'Create Weekly Plan'}</h3>
      </div>

      <div className="text-gray-700 dark:text-gray-300">
        {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">{error}</div>}

        {/* Week Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Week (Monday - Friday)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Start (Monday)</label>
              <input
                type="date"
                className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                value={weekStartDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                required
                disabled={!!plan} // Can't change week dates when editing
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>End (Friday)</label>
              <input
                type="date"
                className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                value={weekEndDate}
                disabled
              />
            </div>
          </div>
        </div>



        {/* Capacity Overview */}
        <div className="bg-gray-50 dark:bg-gray-800 mb-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between mb-1">
            <span className="font-medium">Weekly Capacity (40 hours)</span>
            <span className={`font-semibold ${remainingHours < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {remainingHours.toFixed(1)}h remaining
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{
                width: `${Math.min(percentageUsed, 100)}%`,
                background: percentageUsed > 100 ? '#ef4444' : percentageUsed > 90 ? '#f59e0b' : '#22c55e'
              }}
            />
          </div>
          <div className="flex justify-between mt-1 text-sm text-gray-600 dark:text-gray-400">
            <span>{totalPlanned.toFixed(1)}h planned</span>
            <span>40h capacity</span>
          </div>
        </div>

        {/* Project Allocations */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 m-0">Project Allocations</label>
            <Button type="button" onClick={addProjectAllocation} variant="primary" size="sm">
              + Add Project
            </Button>
          </div>

          {allocations.length === 0 ? (
            <div className="text-center p-6 text-gray-500 dark:text-gray-400">
              No project allocations yet. Click "Add Project" to start planning.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {allocations.map((item, index) => (
                <div key={index} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="grid grid-cols-[2fr_1fr_2fr_auto] gap-2 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Project</label>
                      <select
                        className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        value={item.project_id}
                        onChange={(e) => updateProjectAllocation(index, 'project_id', e.target.value)}
                        required
                      >
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Total Hours</label>
                      <input
                        type="number"
                        className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        value={item.hours}
                        onChange={(e) => updateProjectAllocation(index, 'hours', parseFloat(e.target.value) || 0)}
                        step="0.5"
                        min="0"
                        max="40"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Notes (optional)</label>
                      <input
                        type="text"
                        className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        value={item.notes || ''}
                        onChange={(e) => updateProjectAllocation(index, 'notes', e.target.value)}
                        placeholder="e.g., Sprint 5 tasks"
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={() => removeProjectAllocation(index)}
                      variant="danger"
                      size="sm"
                      style={{ alignSelf: 'end' }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Non-Productive Time Allocations */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 m-0">Non-Productive Time</label>
            <Button type="button" onClick={addNonProductiveAllocation} variant="secondary" size="sm">
              + Add Non-Productive Time
            </Button>
          </div>

          {nonProductiveAllocations.map((item, index) => (
            <div key={index} className="p-4 mb-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Category</label>
                  <select
                    className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    value={item.category_id}
                    onChange={(e) => updateNonProductiveAllocation(index, 'category_id', e.target.value)}
                    required
                  >
                    {NON_PRODUCTIVE_CATEGORY_OPTIONS.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Hours</label>
                  <input
                    type="number"
                    className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    value={item.hours}
                    onChange={(e) => updateNonProductiveAllocation(index, 'hours', parseFloat(e.target.value) || 0)}
                    step="0.5"
                    min="0"
                    max="40"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontSize: 'var(--font-size-sm)' }}>Notes (optional)</label>
                  <input
                    type="text"
                    className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    value={item.notes || ''}
                    onChange={(e) => updateNonProductiveAllocation(index, 'notes', e.target.value)}
                    placeholder="e.g., Team standup daily"
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => removeNonProductiveAllocation(index)}
                  variant="danger"
                  size="sm"
                  style={{ alignSelf: 'end' }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}

          {nonProductiveAllocations.length === 0 && (
            <div className="text-center" style={{ padding: 'var(--spacing-md)', color: 'var(--gray-500)' }}>
              No non-productive time allocated. Click "Add Non-Productive Time" to track meetings, leave, etc.
            </div>
          )}
        </div>

        {/* Overall Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes (optional)</label>
          <textarea
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-vertical min-h-[100px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Overall notes for this week's plan..."
            rows={3}
          />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex gap-2 flex-wrap">
        <Button type="button" onClick={onCancel} variant="outline">
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          {plan ? 'Update Plan' : 'Create Plan'}
        </Button>
      </div>
    </form>
  );
};

export default WeeklyPlanForm;
